import React, { useMemo, useState } from 'react';
import { ArrowLeft, Clock3, ImageOff, Plus, SearchX, Star, Store } from 'lucide-react';
import {
  formatMoney,
  getRestaurantDeliveryLabel,
  isRestaurantOrderable,
  productMatches
} from './restaurantUtils';
import { RestaurantEmptyState, RestaurantSkeletons } from './RestaurantFeedback';

function ProductCard({ product, quantity, onOpen, onQuickAdd }) {
  const canOrder = product.available && product.restaurantOrderable;
  return (
    <article className={`rc-product-card ${!canOrder ? 'is-unavailable' : ''}`}>
      <button type="button" className="rc-product-card__body" onClick={() => onOpen(product)}>
        <div className="rc-product-card__image">
          {product.image_url ? (
            <img src={product.image_url} alt="" loading="lazy" />
          ) : (
            <div className="rc-image-fallback"><ImageOff aria-hidden="true" /></div>
          )}
        </div>
        <div className="rc-product-card__copy">
          <h3>{product.name}</h3>
          {product.description && <p>{product.description}</p>}
          <strong>{formatMoney(product.price)}</strong>
          {!product.available && <span className="rc-sold-out">Agotado</span>}
          {product.available && !product.restaurantOrderable && <span className="rc-sold-out">Delivery no disponible</span>}
        </div>
      </button>
      <button
        type="button"
        className="rc-product-card__add"
        disabled={!canOrder}
        onClick={() => onQuickAdd(product)}
        aria-label={`Agregar ${product.name}`}
      >
        {quantity > 0 ? <b>{quantity}</b> : <Plus aria-hidden="true" />}
      </button>
    </article>
  );
}

export default function RestaurantDetail({
  restaurant,
  menu,
  menuLoading,
  menuError,
  query,
  cart,
  onBack,
  onRetry,
  onOpenProduct,
  onQuickAdd
}) {
  const restaurantOrderable = isRestaurantOrderable(restaurant);
  const [activeTab, setActiveTab] = useState('all');
  const tabs = useMemo(() => [
    { id: 'all', name: 'Menú' },
    { id: 'featured', name: 'Más vendidos', predicate: (item) => item.featured },
    { id: 'low-price', name: 'Precio más bajo', sort: true },
    ...menu.categories.filter((category) => category.active !== false)
      .map((category) => ({ id: category.id, name: category.name, predicate: (item) => item.category_id === category.id }))
  ], [menu.categories]);

  const products = useMemo(() => {
    const tab = tabs.find((item) => item.id === activeTab) || tabs[0];
    let result = menu.items.filter((item) => item.active !== false && productMatches(item, query));
    if (tab.predicate) result = result.filter(tab.predicate);
    if (tab.sort) result = [...result].sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    return result;
  }, [activeTab, menu.items, query, tabs]);

  return (
    <div className="rc-detail">
      <button type="button" className="rc-detail__back" onClick={onBack}>
        <ArrowLeft aria-hidden="true" />Todos los restaurantes
      </button>
      <section className="rc-detail__hero">
        <div className="rc-detail__cover">
          {restaurant.cover_url ? (
            <img src={restaurant.cover_url} alt="" />
          ) : (
            <div className="rc-image-fallback"><Store aria-hidden="true" /><span>PROVI</span></div>
          )}
        </div>
        <div className="rc-detail__summary">
          <div className="rc-detail__logo">
            {restaurant.logo_url ? <img src={restaurant.logo_url} alt="" /> : <Store aria-hidden="true" />}
          </div>
          <div>
            <span className={`rc-status-dot ${restaurantOrderable ? '' : 'is-unavailable'}`}>
              {getRestaurantDeliveryLabel(restaurant)}
            </span>
            <h1>{restaurant.name}</h1>
            {restaurant.description && <p>{restaurant.description}</p>}
            <div className="rc-detail__meta">
              {Number(restaurant.rating) > 0 && <span><Star aria-hidden="true" />{Number(restaurant.rating).toFixed(1)}</span>}
              {restaurant.estimated_time && <span><Clock3 aria-hidden="true" />{restaurant.estimated_time}</span>}
              {restaurant.category && <span>{restaurant.category}</span>}
            </div>
          </div>
          {restaurant.promo_text && <span className="rc-promo-badge">{restaurant.promo_text}</span>}
        </div>
        <div className="rc-fulfillment">
          <button type="button" className={restaurantOrderable ? 'is-active' : ''} disabled={!restaurantOrderable}>Delivery</button>
          <button type="button" disabled>Retiro <small>Próximamente</small></button>
        </div>
      </section>

      <nav className="rc-menu-tabs" aria-label="Filtros del menú">
        {tabs.map((tab) => (
          <button
            type="button"
            key={tab.id}
            className={activeTab === tab.id ? 'is-active' : ''}
            onClick={() => setActiveTab(tab.id)}
            aria-pressed={activeTab === tab.id}
          >
            {tab.name}
          </button>
        ))}
      </nav>

      <section className="rc-section rc-menu-section">
        <div className="rc-section__heading">
          <div><span className="rc-eyebrow">MENÚ REAL</span><h2>{tabs.find((tab) => tab.id === activeTab)?.name}</h2></div>
          <span>{products.length} productos</span>
        </div>
        {menuLoading ? (
          <RestaurantSkeletons count={4} />
        ) : menuError ? (
          <RestaurantEmptyState error title="No pudimos cargar el menú" message={menuError} actionLabel="Reintentar" onAction={onRetry} />
        ) : products.length === 0 ? (
          <RestaurantEmptyState
            title={query ? 'No encontramos ese producto' : 'No hay productos en esta sección'}
            message={query ? 'Prueba con otra búsqueda.' : 'El restaurante aún no publicó productos disponibles aquí.'}
          />
        ) : (
          <div className="rc-product-grid">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={{ ...product, restaurantOrderable }}
                quantity={cart.find((item) => item.menu_item_id === product.id)?.quantity || 0}
                onOpen={onOpenProduct}
                onQuickAdd={onQuickAdd}
              />
            ))}
          </div>
        )}
        {query && products.length > 0 && <p className="rc-search-summary"><SearchX aria-hidden="true" />Productos filtrados por “{query}”</p>}
      </section>
    </div>
  );
}
